import React, { useState, useEffect, useMemo, lazy, Suspense, useTransition } from 'react';
import { useBranch } from '@/contexts/BranchContext';
import { useOfflineOrders } from '@/hooks/useOfflineOrders';
import { useNetwork } from '@/contexts/NetworkContext';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Lock, ClipboardList, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { subscriptionManager } from '@/utils/subscriptionManager';
import { initializeSoundSystem, testNotificationSound } from '@/utils/soundUtils';

// Feature hooks and components
import { useOrderActions } from '@/features/orders/hooks/useOrderActions';
import { useOrderFiltering } from '@/features/orders/hooks/useOrderFiltering';
import { useOrderNotifications } from '@/features/orders/hooks/useOrderNotifications';
import { useItemAcknowledgment } from '@/features/orders/hooks/useItemAcknowledgment';
import { OrdersHeader } from '@/features/orders/components/OrdersHeader';
import { OrderStats } from '@/features/orders/components/OrderStats';
import { OrderFilters } from '@/features/orders/components/OrderFilters';
import { OrderCard } from '@/features/orders/components/OrderCard';

import type { Order } from '@/types/index';

// Lazy load heavy components
const DailySalesReport = lazy(() => import('@/components/DailySalesReport').then(m => ({ default: m.DailySalesReport })));
const AssignRiderDialog = lazy(() => import('@/components/AssignRiderDialog').then(m => ({ default: m.AssignRiderDialog })));
const FastMode = lazy(() => import('@/features/orders/components/FastMode').then(m => ({ default: m.FastMode })));
const OrderDetailsDialog = lazy(() => import('@/features/orders/components/OrderDetailsDialog').then(m => ({ default: m.OrderDetailsDialog })));
const CheckoutDialog = lazy(() => import('@/components/CheckoutDialog').then(m => ({ default: m.CheckoutDialog })));

function Orders() {
  const { selectedBranchId } = useBranch();
  const { branches } = useFilteredBranches();
  const { staffSession, isStaff, isOwner } = useAuth();
  const { toast } = useToast();

  // Global State
  const [isFastMode, setIsFastMode] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('active');
  const [isPending, startTransition] = useTransition();
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [orderColumnsLayout, setOrderColumnsLayout] = useState<1 | 2 | 3>(2);

  const handleTabChange = (value: string) => {
    startTransition(() => {
      setActiveTab(value);
    });
  };

  // Network status
  const { isOnline, isElectronApp } = useNetwork();

  // Data Fetching with offline support
  const {
    orders,
    loading,
    updateOrderStatus,
    refetch,
    isOrderUpdating,
    createOrder,
    clearOptimisticUpdate,
    isOffline: ordersOffline,
    pendingSyncCount
  } = useOfflineOrders(selectedBranchId || undefined);

  const currentBranch = useMemo(() =>
    branches?.find(b => b.id === selectedBranchId),
    [branches, selectedBranchId]
  );

  // Feature Hooks
  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    filteredOrders,
    orderStats
  } = useOrderFiltering(orders);

  const {
    handleQuickAction,
    handleStatusUpdate,
    handleOrderCompleted,
    checkoutDialogOpen,
    setCheckoutDialogOpen,
    orderToComplete,
    setOrderToComplete
  } = useOrderActions({
    branchId: selectedBranchId || undefined,
    orders,
    updateOrderStatus,
    refetch,
    clearOptimisticUpdate,
    soundEnabled
  });

  const {
    deliveryNotificationEnabled,
    setDeliveryNotificationEnabled,
    newDeliveryOrderCount,
    activeOrdersNotificationEnabled,
    setActiveOrdersNotificationEnabled,
    newActiveOrderCount,
    pendingDeliveryOrderCount
  } = useOrderNotifications({
    orders: filteredOrders,
    soundEnabled,
    activeTab,
    loading
  });

  const {
    handleMarkItemSeen,
    checkHasNewItems
  } = useItemAcknowledgment(orders, refetch);

  // Dialog States
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [isRiderDialogOpen, setIsRiderDialogOpen] = useState(false);
  const [selectedOrderForRider, setSelectedOrderForRider] = useState<Order | null>(null);

  // Initialization & Effects
  useEffect(() => {
    // Initialize sound on any user interaction
    const handleUserInteraction = async () => {
      await initializeSoundSystem();
      // Remove listeners after first interaction
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto Refresh Logic
  useEffect(() => {
    if (!autoRefresh || !selectedBranchId) return;

    const isAnyDialogOpen = () => !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');

    if (!isAnyDialogOpen() && isPageVisible) {
      refetch();
    }

    const intervalId = setInterval(() => {
      if (isAnyDialogOpen() || !isPageVisible) return;
      refetch();
    }, 20000);

    return () => clearInterval(intervalId);
  }, [autoRefresh, selectedBranchId, refetch, isPageVisible]);

  // Real-time Subscriptions
  useEffect(() => {
    if (!selectedBranchId) return;

    const ordersChannel = `orders-${selectedBranchId}`;
    const orderItemsChannel = `order-items-${selectedBranchId}`;
    let refetchTimeout: NodeJS.Timeout | null = null;

    const handleRealtimeUpdate = () => {
      if (refetchTimeout) clearTimeout(refetchTimeout);
      refetchTimeout = setTimeout(() => {
        refetch();
      }, 1000);
    };

    subscriptionManager.createChannel(
      ordersChannel,
      {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `branch_id=eq.${selectedBranchId}`
      },
      handleRealtimeUpdate
    );

    subscriptionManager.createChannel(
      orderItemsChannel,
      {
        event: '*',
        schema: 'public',
        table: 'order_items'
      },
      handleRealtimeUpdate
    );

    return () => {
      if (refetchTimeout) clearTimeout(refetchTimeout);
      subscriptionManager.removeChannel(ordersChannel);
      subscriptionManager.removeChannel(orderItemsChannel);
    };
  }, [selectedBranchId, refetch]);

  // Grouping Logic
  const groupedActiveOrders = useMemo(() => {
    const activeOrders = filteredOrders.filter(order =>
      order.order_type !== 'delivery' &&
      ['pending', 'confirmed', 'preparing', 'ready'].includes(order.status)
    );
    const groups: Record<string, Order[]> = {};
    activeOrders.forEach(order => {
      const sessionId = order.table_session_id || order.id;
      if (!groups[sessionId]) groups[sessionId] = [];
      groups[sessionId].push(order);
    });
    return groups;
  }, [filteredOrders]);

  const groupedDeliveryOrders = useMemo(() => {
    const deliveryOrders = filteredOrders.filter(order =>
      order.order_type === 'delivery' &&
      ['pending', 'confirmed', 'preparing', 'ready'].includes(order.status)
    );
    const groups: Record<string, Order[]> = {};
    deliveryOrders.forEach(order => {
      const sessionId = order.table_session_id || order.id;
      if (!groups[sessionId]) groups[sessionId] = [];
      groups[sessionId].push(order);
    });
    return groups;
  }, [filteredOrders]);

  const groupedHistoryOrders = useMemo(() => {
    const historyOrders = filteredOrders.filter(order =>
      ['completed', 'cancelled'].includes(order.status)
    );
    const groups: Record<string, Order[]> = {};
    historyOrders.forEach(order => {
      const sessionId = order.table_session_id || order.id;
      if (!groups[sessionId]) groups[sessionId] = [];
      groups[sessionId].push(order);
    });
    return groups;
  }, [filteredOrders]);

  const canSeeTodayRevenue = isOwner || (staffSession?.access_permissions?.show_today_revenue === true);

  // Handlers
  const handleViewDetails = (data: any) => {
    setSelectedSession(data);
    setIsDetailsOpen(true);
  };

  const handleAssignRider = (order: Order) => {
    setSelectedOrderForRider(order);
    setIsRiderDialogOpen(true);
  };

  // Render
  if (isFastMode) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
        <FastMode
          isFastMode={isFastMode}
          setIsFastMode={setIsFastMode}
          selectedBranchId={selectedBranchId}
          orders={orders}
          createOrder={createOrder}
          refetch={refetch}
          isStaff={isStaff}
          currentBranch={currentBranch}
          isOrderUpdating={isOrderUpdating}
          onQuickAction={handleQuickAction}
          onAssignRider={handleAssignRider}
          onViewDetails={handleViewDetails}
          checkoutDialogOpen={checkoutDialogOpen}
          setCheckoutDialogOpen={setCheckoutDialogOpen}
          orderToComplete={orderToComplete}
          onOrderCompleted={handleOrderCompleted}
        />
      </Suspense>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20">
        <OrdersHeader
          isFastMode={isFastMode}
          setIsFastMode={setIsFastMode}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
          soundEnabled={soundEnabled}
          setSoundEnabled={async (enabled) => {
            if (enabled) await testNotificationSound();
            setSoundEnabled(enabled);
          }}
          onRefresh={refetch}
          isRefetching={loading}
          selectedBranchId={selectedBranchId}
          currentBranch={currentBranch}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />

        {!selectedBranchId ? (
          <Card className="rounded-2xl shadow-lg">
            <CardContent className="text-center py-20 text-gray-500">
              <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">Select a branch to manage orders</h3>
              <p>Choose a location from the dropdown to start managing orders efficiently.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <OrderStats
              stats={orderStats}
              currency={currentBranch?.currency || 'USD'}
              showRevenue={canSeeTodayRevenue}
            />

            <OrderFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              {/* Mobile: 2x2 grid, Desktop: horizontal tabs */}
              <TabsList className="mb-4 md:mb-6 w-full grid grid-cols-2 md:grid-cols-4 h-auto gap-1 p-1">
                <TabsTrigger value="active" className="relative flex items-center justify-center gap-1 text-xs md:text-sm py-2 md:py-2.5 data-[state=active]:font-semibold overflow-visible">
                  <span className="hidden sm:inline">Active</span>
                  <span className="sm:hidden">Active</span>
                  {newActiveOrderCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-red-500 animate-pulse h-5 min-w-5 text-[10px] flex items-center justify-center px-1">{newActiveOrderCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="delivery" className="relative flex items-center justify-center gap-1 text-xs md:text-sm py-2 md:py-2.5 data-[state=active]:font-semibold overflow-visible">
                  <span className="hidden sm:inline">Delivery</span>
                  <span className="sm:hidden">Delivery</span>
                  {pendingDeliveryOrderCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-blue-600 h-5 min-w-5 text-[10px] flex items-center justify-center px-1">{pendingDeliveryOrderCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="history" className="text-xs md:text-sm py-2 md:py-2.5 data-[state=active]:font-semibold">History</TabsTrigger>
                <TabsTrigger value="sales" className="text-xs md:text-sm py-2 md:py-2.5 data-[state=active]:font-semibold">Sales</TabsTrigger>
              </TabsList>

              {/* Active Orders Tab */}
              <TabsContent value="active" className="space-y-4">
                {/* Column layout switcher - hidden on mobile */}
                <div className="hidden md:flex justify-end items-center mb-4">
                  <div className="flex gap-1">
                    {[1, 2, 3].map(cols => (
                      <Button
                        key={cols}
                        size="sm"
                        variant={orderColumnsLayout === cols ? 'default' : 'outline'}
                        onClick={() => setOrderColumnsLayout(cols as 1 | 2 | 3)}
                        className="text-xs px-3"
                      >
                        {cols} Col
                      </Button>
                    ))}
                  </div>
                </div>

                {loading && !orders.length ? (
                  <div className={`grid ${orderColumnsLayout === 1 ? 'grid-cols-1' : orderColumnsLayout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-4`}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} className="h-[300px] w-full rounded-xl" />
                    ))}
                  </div>
                ) : Object.keys(groupedActiveOrders).length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-gray-500">No active orders found.</CardContent></Card>
                ) : (
                  <div className={`grid ${orderColumnsLayout === 1 ? 'grid-cols-1' : orderColumnsLayout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-4`}>
                    {Object.entries(groupedActiveOrders).map(([sessionId, sessionOrders]) => (
                      <OrderCard
                        key={sessionId}
                        sessionId={sessionId}
                        sessionOrders={sessionOrders}
                        latestOrder={sessionOrders[0]}
                        isOrderUpdating={isOrderUpdating}
                        onQuickAction={handleQuickAction}
                        onRefetch={refetch}
                        currentBranch={currentBranch}
                        isStaff={isStaff}
                        onViewDetails={handleViewDetails}
                        hasNewItems={checkHasNewItems(sessionId, sessionOrders)}
                        onMarkItemSeen={handleMarkItemSeen}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Delivery Orders Tab */}
              <TabsContent value="delivery" className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="delivery-notifications"
                      checked={deliveryNotificationEnabled}
                      onCheckedChange={setDeliveryNotificationEnabled}
                    />
                    <Label htmlFor="delivery-notifications" className="text-sm text-gray-600">
                      Delivery Alerts
                    </Label>
                  </div>
                  {/* Column layout switcher - hidden on mobile */}
                  <div className="hidden md:flex gap-1">
                    {[1, 2, 3].map(cols => (
                      <Button
                        key={cols}
                        size="sm"
                        variant={orderColumnsLayout === cols ? 'default' : 'outline'}
                        onClick={() => setOrderColumnsLayout(cols as 1 | 2 | 3)}
                        className="text-xs px-3"
                      >
                        {cols} Col
                      </Button>
                    ))}
                  </div>
                </div>

                {loading && !orders.length ? (
                  <div className={`grid ${orderColumnsLayout === 1 ? 'grid-cols-1' : orderColumnsLayout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-4`}>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-[300px] w-full rounded-xl" />
                    ))}
                  </div>
                ) : Object.keys(groupedDeliveryOrders).length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-gray-500">No delivery orders found.</CardContent></Card>
                ) : (
                  <div className={`grid ${orderColumnsLayout === 1 ? 'grid-cols-1' : orderColumnsLayout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-4`}>
                    {Object.entries(groupedDeliveryOrders).map(([sessionId, sessionOrders]) => (
                      <OrderCard
                        key={sessionId}
                        sessionId={sessionId}
                        sessionOrders={sessionOrders}
                        latestOrder={sessionOrders[0]}
                        isOrderUpdating={isOrderUpdating}
                        onQuickAction={handleQuickAction}
                        onRefetch={refetch}
                        currentBranch={currentBranch}
                        isStaff={isStaff}
                        onAssignRider={handleAssignRider}
                        onViewDetails={handleViewDetails}
                        hasNewItems={checkHasNewItems(sessionId, sessionOrders)}
                        onMarkItemSeen={handleMarkItemSeen}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history">
                {(isOwner || staffSession?.access_permissions?.order_history) ? (
                  loading && !orders.length ? (
                    <div className={`grid ${orderColumnsLayout === 1 ? 'grid-cols-1' : orderColumnsLayout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-4`}>
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-[300px] w-full rounded-xl" />
                      ))}
                    </div>
                  ) : Object.keys(groupedHistoryOrders).length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-gray-500">No order history found.</CardContent></Card>
                  ) : (
                    <div className={`grid ${orderColumnsLayout === 1 ? 'grid-cols-1' : orderColumnsLayout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-4`}>
                      {Object.entries(groupedHistoryOrders).map(([sessionId, sessionOrders]) => (
                        <OrderCard
                          key={sessionId}
                          sessionId={sessionId}
                          sessionOrders={sessionOrders}
                          latestOrder={sessionOrders[0]}
                          isOrderUpdating={isOrderUpdating}
                          onQuickAction={handleQuickAction}
                          onRefetch={refetch}
                          currentBranch={currentBranch}
                          isStaff={isStaff}
                          onViewDetails={handleViewDetails}
                        />
                      ))}
                    </div>
                  )
                ) : (
                  <Card className="rounded-2xl">
                    <CardContent className="text-center py-16 text-gray-500">
                      <Lock className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
                      <p>You don't have permission to view order history.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Sales Tab */}
              <TabsContent value="sales">
                {(isOwner || staffSession?.access_permissions?.daily_sales_report) ? (
                  <div className="mt-4">
                    <Suspense fallback={<div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>}>
                      <DailySalesReport
                        branchId={selectedBranchId}
                        currency={currentBranch?.currency || 'USD'}
                      />
                    </Suspense>
                  </div>
                ) : (
                  <Card className="rounded-2xl">
                    <CardContent className="text-center py-16 text-gray-500">
                      <Lock className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
                      <p>You don't have permission to view daily sales reports.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}

        <Suspense fallback={null}>
          <OrderDetailsDialog
            open={isDetailsOpen}
            onOpenChange={setIsDetailsOpen}
            selectedSession={selectedSession}
            currentBranch={currentBranch}
            onStatusUpdate={(orderId, status) => handleStatusUpdate(orderId, status, selectedSession?.sessionId)}
          />

          {orderToComplete && (
            <CheckoutDialog
              open={checkoutDialogOpen}
              onOpenChange={setCheckoutDialogOpen}
              order={{
                id: orderToComplete.id,
                order_number: orderToComplete.order_number,
                customer_name: orderToComplete.customer_name,
                total_amount: orderToComplete.total_amount,
                subtotal_amount: orderToComplete.total_amount,
                service_amount: 0,
                tax_amount: 0,
                delivery_fee: orderToComplete.delivery_fee || 0,
                branch_id: selectedBranchId || orderToComplete.branch_id,
                table_session_id: orderToComplete.table_session_id,
                order_type: orderToComplete.order_type,
                table_number: orderToComplete.table_number
              }}
              currency={currentBranch?.currency || 'USD'}
              onOrderCompleted={handleOrderCompleted}
            />
          )}

          <AssignRiderDialog
            open={isRiderDialogOpen}
            onOpenChange={setIsRiderDialogOpen}
            order={selectedOrderForRider}
            onAssigned={() => {
              refetch();
              toast({
                title: "Rider Assigned",
                description: `Order #${selectedOrderForRider?.order_number} has been assigned to a delivery rider.`,
              });
            }}
          />
        </Suspense>
      </div>
    </DashboardLayout>
  );
}

export default Orders;
